// Vistas del módulo de inventario.
//
// Cuatro pantallas, cada una respondiendo una pregunta que el administrador
// se hace de verdad: qué hay, qué está por caducar, qué está en tránsito y
// qué hay que resurtir.

import { useCallback, useEffect, useState } from 'react';
import {
  AlertTriangle,
  ArrowLeftRight,
  Boxes,
  CalendarClock,
  ClipboardList,
  Loader2,
  PackagePlus,
} from 'lucide-react';

import { inventoryApi, InventoryApiError } from './api';
import { EntryForm, ExitForm, NewSupplyForm, TransferForm } from './forms';
import type { ExpiringLot, Movement, Stock, SupplyRequest } from './types';
import { posApi } from '@/modules/pos';
import type { Location } from '@/modules/pos';

const num = (v: string): string => Number(v).toLocaleString('es-MX', { maximumFractionDigits: 3 });
const fecha = (iso: string): string => new Date(iso).toLocaleDateString('es-MX');

function Shell({
  icon: Icon,
  title,
  subtitle,
  action,
  children,
}: {
  icon: typeof Boxes;
  title: string;
  subtitle: string;
  action?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div className="p-4 sm:p-6 max-w-6xl mx-auto">
      <div className="flex items-start justify-between gap-3 mb-5 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-primary/15 flex items-center justify-center">
            <Icon className="w-5 h-5 text-primary" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-foreground leading-tight">{title}</h1>
            <p className="text-sm text-muted-foreground">{subtitle}</p>
          </div>
        </div>
        {action}
      </div>
      {children}
    </div>
  );
}

function ErrorBox({ error }: { error: string }) {
  return (
    <div className="mb-4 px-4 py-3 rounded-lg bg-red-500/10 border border-red-500/30 text-sm text-red-500">
      {error}
    </div>
  );
}

const Spinner = () => (
  <div className="flex items-center justify-center py-20 text-muted-foreground">
    <Loader2 className="w-6 h-6 animate-spin" />
  </div>
);

function Empty({ icon: Icon, title, hint }: { icon: typeof Boxes; title: string; hint: string }) {
  return (
    <div className="flex flex-col items-center justify-center py-16 text-center">
      <Icon className="w-10 h-10 text-muted-foreground mb-3" />
      <p className="text-foreground font-medium">{title}</p>
      <p className="text-sm text-muted-foreground mt-1 max-w-md">{hint}</p>
    </div>
  );
}

function useLocations() {
  const [locations, setLocations] = useState<Location[] | null>(null);
  useEffect(() => {
    posApi.locations().then(setLocations).catch(() => setLocations([]));
  }, []);
  return locations;
}

function LocationPicker({
  locations,
  value,
  onChange,
}: {
  locations: Location[];
  value: string;
  onChange: (id: string) => void;
}) {
  return (
    <div className="flex flex-wrap gap-2 mb-5">
      {locations.map((l) => (
        <button
          key={l.id}
          type="button"
          onClick={() => onChange(l.id)}
          className={`px-3 py-1.5 rounded-lg border text-sm ${
            l.id === value
              ? 'border-primary bg-primary/15 text-foreground font-medium'
              : 'border-border bg-card text-muted-foreground hover:text-foreground'
          }`}
        >
          {l.name}
        </button>
      ))}
    </div>
  );
}

// ── Existencias ─────────────────────────────────────────────────

export function InventoryStockView() {
  const locations = useLocations();
  const [locationId, setLocationId] = useState('');
  const [stock, setStock] = useState<Stock[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [abierto, setAbierto] = useState<string | null>(null);
  const [tick, setTick] = useState(0);
  const reload = () => setTick((t) => t + 1);

  useEffect(() => {
    if (!locationId && locations?.length) setLocationId(locations[0].id);
  }, [locations, locationId]);

  const cargar = useCallback(() => {
    if (!locationId) return;
    setError(null);
    inventoryApi
      .stock(locationId)
      .then(setStock)
      .catch((e: InventoryApiError) => setError(e.message));
  }, [locationId]);

  useEffect(cargar, [cargar, tick]);

  if (!locations) return <Shell icon={Boxes} title="Existencias" subtitle="Qué hay en cada punto"><Spinner /></Shell>;
  if (!locations.length) {
    return (
      <Shell icon={Boxes} title="Existencias" subtitle="Qué hay en cada punto">
        <Empty icon={Boxes} title="No hay puntos dados de alta" hint="El inventario cuelga de un punto. Créalos en Mostrador → Puntos." />
      </Shell>
    );
  }

  return (
    <Shell
      icon={Boxes}
      title="Existencias"
      subtitle="Lo que hay, lote por lote, en orden de uso"
      action={
        <div className="flex gap-2 flex-wrap">
          <NewSupplyForm onDone={reload} />
          <EntryForm locations={locations} onDone={reload} />
          <ExitForm locations={locations} onDone={reload} />
        </div>
      }
    >
      <LocationPicker locations={locations} value={locationId} onChange={setLocationId} />
      {error && <ErrorBox error={error} />}
      {!stock && !error && <Spinner />}

      {stock && !stock.length && (
        <Empty icon={PackagePlus} title="Sin insumos en el catálogo" hint="Da de alta un insumo y regístrale su primera entrada." />
      )}

      <div className="grid gap-2">
        {stock?.map((s) => (
          <div key={s.supply_id} className="rounded-xl border border-border bg-card">
            <button
              type="button"
              onClick={() => setAbierto(abierto === s.supply_id ? null : s.supply_id)}
              className="w-full p-4 flex items-center justify-between gap-3 text-left"
            >
              <div>
                <p className="font-medium text-foreground">{s.supply_name}</p>
                <p className="text-xs text-muted-foreground">
                  {s.lots.length} {s.lots.length === 1 ? 'lote' : 'lotes'}
                  {s.reorder_point !== null && ` · pedir al llegar a ${num(s.reorder_point)}`}
                </p>
              </div>
              <div className="text-right flex items-center gap-3">
                {s.below_reorder && (
                  <span className="text-xs px-2 py-1 rounded-lg bg-amber-500/10 border border-amber-500/30 text-amber-500 whitespace-nowrap">
                    hay que pedir
                  </span>
                )}
                <span className="font-semibold text-foreground tabular-nums whitespace-nowrap">
                  {num(s.total)} {s.unit}
                </span>
              </div>
            </button>

            {abierto === s.supply_id && (
              <div className="border-t border-border px-4 py-3">
                {!s.lots.length ? (
                  <p className="text-sm text-muted-foreground">Sin existencia.</p>
                ) : (
                  <>
                    <p className="text-xs text-muted-foreground mb-2">
                      En orden de uso — el primero es el que toca gastar.
                    </p>
                    <div className="grid gap-1.5">
                      {s.lots.map((l, i) => (
                        <div
                          key={l.id}
                          className={`flex items-center justify-between text-sm rounded-lg px-3 py-2 ${
                            i === 0 ? 'bg-primary/10 border border-primary/30' : 'bg-background'
                          }`}
                        >
                          <span className="text-muted-foreground">
                            {i === 0 && <strong className="text-primary mr-1">USAR ESTE →</strong>}
                            {l.expires_on ? `caduca ${fecha(l.expires_on)}` : 'sin caducidad'}
                          </span>
                          <span className="tabular-nums text-foreground">
                            {num(l.quantity)} {s.unit}
                          </span>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    </Shell>
  );
}

// ── Por caducar ─────────────────────────────────────────────────

export function InventoryExpiringView() {
  const [lotes, setLotes] = useState<ExpiringLot[] | null>(null);
  const [dias, setDias] = useState(7);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setLotes(null);
    inventoryApi
      .expiring(dias)
      .then(setLotes)
      .catch((e: InventoryApiError) => setError(e.message));
  }, [dias]);

  return (
    <Shell icon={CalendarClock} title="Por caducar" subtitle="La lista que evita tirar producto">
      <div className="flex gap-2 mb-5">
        {[3, 7, 15, 30].map((d) => (
          <button
            key={d}
            type="button"
            onClick={() => setDias(d)}
            className={`px-3 py-1.5 rounded-lg border text-sm ${
              d === dias
                ? 'border-primary bg-primary/15 text-foreground font-medium'
                : 'border-border bg-card text-muted-foreground'
            }`}
          >
            {d} días
          </button>
        ))}
      </div>

      {error && <ErrorBox error={error} />}
      {!lotes && !error && <Spinner />}
      {lotes && !lotes.length && (
        <Empty icon={CalendarClock} title={`Nada caduca en ${dias} días`} hint="Buena señal." />
      )}

      <div className="grid gap-2">
        {lotes?.map((l) => {
          const vencido = l.dias_restantes < 0;
          const urgente = l.dias_restantes <= 2;
          return (
            <div
              key={l.lot_id}
              className={`rounded-xl border p-4 flex items-center justify-between gap-3 ${
                vencido
                  ? 'border-red-500/40 bg-red-500/10'
                  : urgente
                    ? 'border-amber-500/40 bg-amber-500/10'
                    : 'border-border bg-card'
              }`}
            >
              <div>
                <p className="font-medium text-foreground">{l.supply}</p>
                <p className="text-xs text-muted-foreground">
                  {l.location} · {num(l.quantity)} {l.unit}
                </p>
              </div>
              <div className="text-right">
                <p className="text-sm font-semibold text-foreground">{fecha(l.expires_on)}</p>
                <p className="text-xs text-muted-foreground">
                  {vencido
                    ? `venció hace ${Math.abs(l.dias_restantes)} d`
                    : l.dias_restantes === 0
                      ? 'caduca hoy'
                      : `faltan ${l.dias_restantes} d`}
                </p>
              </div>
            </div>
          );
        })}
      </div>
    </Shell>
  );
}

// ── Traslados ───────────────────────────────────────────────────

export function InventoryTransfersView() {
  const locations = useLocations();
  const [movs, setMovs] = useState<Movement[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [confirmando, setConfirmando] = useState<string | null>(null);
  const [quien, setQuien] = useState('');
  const [tick, setTick] = useState(0);
  const reload = () => setTick((t) => t + 1);

  useEffect(() => {
    setError(null);
    inventoryApi
      .movements({ kind: 'transfer' })
      .then(setMovs)
      .catch((e: InventoryApiError) => setError(e.message));
  }, [tick]);

  const nombre = (id: string | null) =>
    (locations || []).find((l) => l.id === id)?.name || '—';

  const confirmar = async (id: string) => {
    setError(null);
    try {
      await inventoryApi.confirmTransfer(id, quien.trim());
      setConfirmando(null);
      setQuien('');
      reload();
    } catch (e) {
      setError(e instanceof InventoryApiError ? e.message : 'No se pudo confirmar');
    }
  };

  return (
    <Shell
      icon={ArrowLeftRight}
      title="Traslados"
      subtitle="Del almacén a los puntos, con doble confirmación"
      action={locations ? <TransferForm locations={locations} onDone={reload} /> : null}
    >
      {error && <ErrorBox error={error} />}
      {!movs && !error && <Spinner />}
      {movs && !movs.length && (
        <Empty icon={ArrowLeftRight} title="Sin traslados" hint="Un traslado descuenta del origen al autorizarse y llega al destino solo cuando alguien más lo confirma." />
      )}

      <div className="grid gap-2">
        {movs?.map((m) => (
          <div key={m.id} className="rounded-xl border border-border bg-card p-4">
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div>
                <p className="font-medium text-foreground">
                  {num(m.quantity)} · {m.supply_name}
                </p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {nombre(m.origin_id)} → {nombre(m.destination_id)} · entregó {m.authorized_by}
                  {m.confirmed_by && ` · recibió ${m.confirmed_by}`}
                </p>
              </div>
              {m.status === 'pending' ? (
                <span className="text-xs px-2 py-1 rounded-full bg-amber-500/15 text-amber-500 border border-amber-500/30">
                  En tránsito
                </span>
              ) : (
                <span className="text-xs px-2 py-1 rounded-full bg-emerald-500/15 text-emerald-500 border border-emerald-500/30">
                  Recibido
                </span>
              )}
            </div>

            {m.status === 'pending' && (
              <div className="mt-3 border-t border-border pt-3">
                {confirmando === m.id ? (
                  <div className="flex items-center gap-2 flex-wrap">
                    <input
                      className="rounded-lg border border-border bg-background px-3 py-2 text-sm max-w-xs"
                      placeholder="¿Quién lo recibe?"
                      value={quien}
                      onChange={(e) => setQuien(e.target.value)}
                      autoFocus
                    />
                    <button
                      type="button"
                      disabled={!quien.trim()}
                      onClick={() => void confirmar(m.id)}
                      className="rounded-lg bg-primary px-3 py-2 text-sm font-medium text-primary-foreground disabled:opacity-40"
                    >
                      Confirmar recepción
                    </button>
                    <button
                      type="button"
                      onClick={() => setConfirmando(null)}
                      className="rounded-lg border border-border px-3 py-2 text-sm text-muted-foreground"
                    >
                      Cancelar
                    </button>
                    {/* El backend rechaza que sea la misma persona; se dice antes. */}
                    <p className="w-full text-xs text-muted-foreground">
                      Tiene que ser alguien distinto de quien entregó ({m.authorized_by}).
                    </p>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => {
                      setConfirmando(m.id);
                      setQuien('');
                    }}
                    className="rounded-lg border border-border bg-card px-3 py-2 text-xs text-muted-foreground hover:text-foreground"
                  >
                    Confirmar recepción
                  </button>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    </Shell>
  );
}

// ── Solicitudes ─────────────────────────────────────────────────

export function InventoryRequestsView() {
  const locations = useLocations();
  const [reqs, setReqs] = useState<SupplyRequest[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    inventoryApi
      .requests('open')
      .then(setReqs)
      .catch((e: InventoryApiError) => setError(e.message));
  }, []);

  const nombre = (id: string) => (locations || []).find((l) => l.id === id)?.name || '—';

  return (
    <Shell icon={ClipboardList} title="Solicitudes" subtitle="Lo que hay que resurtir">
      {error && <ErrorBox error={error} />}
      {!reqs && !error && <Spinner />}
      {reqs && !reqs.length && (
        <Empty
          icon={ClipboardList}
          title="Nada pendiente de resurtir"
          hint="Las solicitudes se generan solas cuando un insumo cae a su umbral, y se cierran solas al reabastecer."
        />
      )}

      <div className="grid gap-2">
        {reqs?.map((r) => (
          <div key={r.id} className="rounded-xl border border-border bg-card p-4 flex items-center justify-between gap-3">
            <div>
              <p className="font-medium text-foreground">{r.supply_name}</p>
              <p className="text-xs text-muted-foreground">
                {nombre(r.location_id)} · {fecha(r.created_at)}
                {r.auto_generated && ' · generada automáticamente'}
              </p>
            </div>
            <div className="flex items-center gap-2">
              {r.auto_generated && <AlertTriangle className="w-4 h-4 text-amber-500" />}
              <span className="font-semibold text-foreground tabular-nums">{num(r.quantity)}</span>
            </div>
          </div>
        ))}
      </div>
    </Shell>
  );
}
